import type { Props as SelectProps, SingleValue } from "react-select";

import { SelectInput } from "components/Select";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { getSelectClasses } from "@/utils/filter-styles";

import {
  DATE_FILTER_BUTTON_CLASS,
  DEPOSIT_FILTER_PLACEHOLDERS,
  DEPOSIT_STATUS_OPTIONS,
  FILTER_GROUP_LABELS,
} from "../constants";
import type { DepositFilterKey, DepositFilterValues, OptionType } from "../types";
import { normalizeAmount } from "../utils/filter-input";
import { textFilterFactory } from "./text-filter-factory";

const FilterSelect = SelectInput as (props: SelectProps<OptionType, false>) => JSX.Element;

type FilterChange = (key: DepositFilterKey, value: string | undefined) => void;

interface DepositFiltersProps {
  filters: DepositFilterValues;
  onFilterChange: FilterChange;
  bankOptions?: readonly OptionType[];
  periodStart?: string;
  periodEnd?: string;
}

function selectedOption(
  options: readonly OptionType[],
  value: string | undefined,
): OptionType | null {
  return options.find(option => option.value === value) ?? null;
}

export default function DepositFilters({
  filters,
  onFilterChange,
  bankOptions = [],
  periodStart,
  periodEnd,
}: DepositFiltersProps): JSX.Element {
  const currentBank: OptionType | null = selectedOption(bankOptions, filters.depositBankCode);
  const currentStatus: OptionType | null = selectedOption(
    DEPOSIT_STATUS_OPTIONS,
    filters.depositStatus,
  );

  const textFilter = textFilterFactory(filters, onFilterChange);

  const handleDateChange = (start: string | undefined, end: string | undefined): void => {
    onFilterChange("depositDateStart", start);
    onFilterChange("depositDateEnd", end);
  };

  return (
    <div
      role="group"
      aria-label={FILTER_GROUP_LABELS.DEPOSIT}
      className="grid grid-cols-2 gap-2 xl:grid-cols-4"
    >
      <div data-filter="bank" className="min-w-0">
        <FilterSelect
          aria-label={DEPOSIT_FILTER_PLACEHOLDERS.BANK}
          options={[...bankOptions]}
          placeholder={DEPOSIT_FILTER_PLACEHOLDERS.BANK}
          value={currentBank}
          onChange={(option: SingleValue<OptionType>) =>
            onFilterChange("depositBankCode", option?.value)
          }
          className={`w-full ${getSelectClasses(currentBank !== null)}`}
          classNamePrefix="an-simple-select"
          isClearable
          closeMenuOnSelect
        />
      </div>
      <div data-filter="agency" className="min-w-0">
        {textFilter("depositAgency", DEPOSIT_FILTER_PLACEHOLDERS.AGENCY)}
      </div>
      <div data-filter="account" className="min-w-0">
        {textFilter("depositAccount", DEPOSIT_FILTER_PLACEHOLDERS.ACCOUNT)}
      </div>
      <div data-filter="date" className="min-w-0">
        <PeriodRangeFilter
          start={filters.depositDateStart}
          end={filters.depositDateEnd}
          onChange={handleDateChange}
          label={DEPOSIT_FILTER_PLACEHOLDERS.DATE}
          minDate={periodStart}
          maxDate={periodEnd}
          buttonClassName={DATE_FILTER_BUTTON_CLASS}
          inline
        />
      </div>
      <div data-filter="amount" className="min-w-0">
        {textFilter("depositAmount", DEPOSIT_FILTER_PLACEHOLDERS.AMOUNT, normalizeAmount)}
      </div>
      <div data-filter="description" className="min-w-0 xl:col-span-2">
        {textFilter("depositDescription", DEPOSIT_FILTER_PLACEHOLDERS.DESCRIPTION)}
      </div>
      <div data-filter="status" className="min-w-0">
        <FilterSelect
          aria-label={DEPOSIT_FILTER_PLACEHOLDERS.STATUS}
          options={[...DEPOSIT_STATUS_OPTIONS]}
          placeholder={DEPOSIT_FILTER_PLACEHOLDERS.STATUS}
          value={currentStatus}
          onChange={(option: SingleValue<OptionType>) =>
            onFilterChange("depositStatus", option?.value)
          }
          className={`w-full ${getSelectClasses(currentStatus !== null)}`}
          classNamePrefix="an-simple-select"
          isClearable
          closeMenuOnSelect
        />
      </div>
    </div>
  );
}
